import React, { useCallback, useEffect, useState } from 'react';

import { GeoJSON, MapContainer, Marker, Popup, TileLayer, useMap } from '@monsonjeremy/react-leaflet';

import L from "leaflet";
import useSupercluster from "use-supercluster";

import markerIcon from '../../images/marker.png';

import geoJsonFile from '../../GEOJson/italy_region_geojson.json';
import { UfficioMarker } from './UfficioMarker';

const LocationMarker = ({ latLng }) => {

    const map = useMap();

    return !(map && latLng && latLng.coords.latitude && latLng.coords.longitude) ? null : (
        <Marker key={"current-location"} position={{ lat: latLng.coords.latitude, lng: latLng.coords.longitude }}>
            <Popup>
                Posizione attuale
            </Popup>
        </Marker >
    );
}

const MapOptions = ({ onMoveEnd, ...props }) => {
    const map = useMap();

    useEffect(() => {
        map.attributionControl.setPrefix(null);
        map.closePopupOnClick = false;
        map.on('moveend', onMoveEnd);
    }, [])

    return null;
}

const randomIcon = (iconUrl) => new L.Icon({
    iconUrl: iconUrl ?? markerIcon,
    iconSize: [30, 30]
});

const icons = {};
const fetchIcon = (count, size) => {
    if (!icons[count]) {
        icons[count] = L.divIcon({
            html: `<div class="cluster-marker" style="width: ${size}px; height: ${size}px; color: #fff;
            background: #1978c8;
            border-radius: 50%;
            padding: 10px;
            width: 10px;
            height: 10px;
            margin-left: -10px;
            margin-top: -10px;
            display: flex;
            justify-content: center;
            align-items: center;">
          ${count}
        </div>`
        });
    }
    return icons[count];
};

const UfficiMarker = ({ uffici, markerRefs, onUfficioMarkerClick }) => {
    const maxZoom = 22;
    const [bounds, setBounds] = useState(null);
    const [zoom, setZoom] = useState(12);
    const map = useMap();

    // get map bounds
    function updateMap() {
        const b = map.getBounds();
        setBounds([
            b.getSouthWest().lng,
            b.getSouthWest().lat,
            b.getNorthEast().lng,
            b.getNorthEast().lat,
        ]);
        setZoom(map.getZoom());
    }

    const onMove = useCallback(() => {
        updateMap();
    }, [map]);

    React.useEffect(() => {
        updateMap();
    }, [map]);

    useEffect(() => {
        map.on("move", onMove);
        return () => {
            map.off("move", onMove);
        };
    }, [map, onMove]);

    const points = uffici.map((ufficio) => ({
        type: "Feature",
        data: { ...ufficio },
        properties: { cluster: false, ufficioId: ufficio.id },
        geometry: {
            type: "Point",
            coordinates: [
                parseFloat(ufficio.longitudine),
                parseFloat(ufficio.latitudine),
            ],
        },
    }));

    const { clusters, supercluster } = useSupercluster({
        points: points,
        bounds: bounds,
        zoom: zoom,
        options: { radius: 75, maxZoom: 17 },
    });

    return clusters.map((cluster) => {
        // every cluster point has coordinates
        const [longitude, latitude] = cluster.geometry.coordinates;
        // the point may be either a cluster or a crime point
        const { cluster: isCluster, point_count: pointCount } =
            cluster.properties;

        // we have a cluster to render
        if (isCluster) {
            return (
                <Marker
                    key={`cluster-${cluster.id}`}
                    ref={e => {
                        if (e)
                            markerRefs.current[cluster.id] = e
                    }}
                    position={[latitude, longitude]}
                    icon={fetchIcon(
                        pointCount,
                        10 + (pointCount / points.length) * 40
                    )}
                    eventHandlers={{
                        click: () => {
                            const expansionZoom = Math.min(
                                supercluster.getClusterExpansionZoom(cluster.id),
                                maxZoom
                            );
                            map.setView([latitude, longitude], expansionZoom, {
                                animate: true,
                            });
                        },
                    }}
                />
            );
        }

        return (
            <UfficioMarker
                onMarkerClick={onUfficioMarkerClick}
                icon={randomIcon(cluster.data?.tipoUfficio?.urlIcona)}
                key={`ufficio-${cluster.data.id}`}
                ufficiData={uffici}
                ufficioData={cluster.data}
                markerRefs={markerRefs}
            />
        );
    }
    );
}


export const UfficioMap = ({ ufficiData, mapRef, markerRefs, initialZoom, currentUserLocation, onUfficioMarkerClick, ...props }) => {

    const [zoom, setZoom] = useState(initialZoom);

    function updateMap() {
        setZoom(mapRef.current.getZoom());
    }

    let mapEdgeMinLongitude = ufficiData ? Math.min(...ufficiData?.map(x => x.longitudine ?? 0)) : 12.5;
    let mapEdgeMaxLongitude = ufficiData ? Math.max(...ufficiData?.map(x => x.longitudine ?? 0)) : 12.5;

    let mapEdgeMinLatitude = ufficiData ? Math.min(...ufficiData?.map(x => x.latitudine ?? 0)) : 42;
    let mapEdgeMaxLatitude = ufficiData ? Math.max(...ufficiData?.map(x => x.latitudine ?? 0)) : 42;

    if (mapEdgeMinLongitude === Infinity && mapEdgeMaxLongitude === -Infinity) {
        mapEdgeMinLongitude = 12.5;
        mapEdgeMaxLongitude = 12.5;
    }

    if (mapEdgeMinLatitude === Infinity && mapEdgeMaxLatitude === -Infinity) {
        mapEdgeMinLatitude = 42;
        mapEdgeMaxLatitude = 42;
    }

    const maxMarkersDistance = (mapEdgeMaxLatitude - mapEdgeMinLatitude) > (mapEdgeMaxLongitude - mapEdgeMinLongitude) ? (mapEdgeMaxLatitude - mapEdgeMinLatitude) : (mapEdgeMaxLongitude - mapEdgeMinLongitude);

    return (
        <>
            <MapContainer
                center={[(mapEdgeMinLatitude + mapEdgeMaxLatitude) / 2, (mapEdgeMinLongitude + mapEdgeMaxLongitude) / 2]}
                zoom={zoom ?? 6 + Math.log(2) / Math.log(9 / maxMarkersDistance)}
                minZoom={5}
                scrollWheelZoom={false}
                whenCreated={map => {
                    if (mapRef) {
                        mapRef.current = map;
                        updateMap();
                    }
                }}

                style={{ height: "100%", width: "100%" }}
                className="markercluster-map"
            >
                <MapOptions />
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {/* <SVGOverlay>
                    <svg viewBox="0 0 220 100" xmlns="http://www.w3.org/2000/svg" src="https://it.wikiversity.org/wiki/File:Italian_regions_provinces_white_no_labels.svg">

                    </svg>
                </SVGOverlay> */}
                <GeoJSON data={geoJsonFile} style={{
                    fillColor: "transparent",
                    weight: "1",
                    color: '#004d40'
                }} />
                <UfficiMarker uffici={ufficiData} markerRefs={markerRefs} onUfficioMarkerClick={onUfficioMarkerClick} />
                <LocationMarker latLng={currentUserLocation} />
            </MapContainer>
        </>
    );
}

